import { useEffect, useRef } from 'react';

import { useStreamCursor } from './context';

/**
 * MV-2116 — applies the cursor UE reports to a stream whose `<video>` the page
 * does not render itself (the PixelStreaming providers: `MYTA_STREAMS`,
 * `COREWEAVE`).
 *
 * GameCast can do this in JSX because the hub owns that `<video>`; here the
 * element is created by `@epicgames-ps/lib-pixelstreamingfrontend` inside its
 * `videoElementParent`, so the write has to be imperative.
 *
 * It goes on the `<video>` and not on the container for a specific reason: the
 * library rewrites the container's *entire* inline style on every resize —
 * `VideoPlayer.resizePlayerStyleToFillParentElement` ends its `setAttribute`
 * with `cursor: default` — and both the wrapper and the library fire `resize`
 * on mount, on play and on reconnect. A value on the container would be wiped,
 * and an inherited one would lose to that `default` anyway. Nothing ever
 * rewrites the `<video>`'s own style attribute after construction (it only sets
 * width/height/position/pointerEvents once), so the inline cursor there sticks
 * and beats the inherited `default`.
 *
 * Re-applied through a `MutationObserver` rather than once, because a reconnect
 * inside the same session builds a new `WebRtcPlayerController` and with it a
 * `new VideoPlayer(pixelStreaming.videoElementParent, …)` — a *new* `<video>`
 * under the *same* root. Without the observer the cursor would stay on the
 * destroyed element until UE happened to report a different state. Every
 * `<video>` under the root is written to, so the moment during a swap when the
 * old and the new one coexist cannot leave the live element unstyled.
 *
 * @param streamRoot - The library's `videoElementParent` for the active stream
 * (`rootElement`), or null before it exists. Deliberately not a document-wide
 * lookup: the other providers' tags are in the DOM too.
 */
export const useApplyStreamCursor = (streamRoot: HTMLElement | null) => {
  const cursor = useStreamCursor((state) => state.cursor);

  // The observer callback must see the current value without being rebuilt for
  // it; the effect below re-applies on change on its own.
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  useEffect(() => {
    if (!streamRoot) {
      return undefined;
    }

    const apply = () => {
      streamRoot.querySelectorAll('video').forEach((video) => {
        video.style.cursor = cursorRef.current;
      });
    };

    apply();

    // childList only: `apply` writes a style attribute, and observing
    // attributes as well would make it re-enter on its own writes.
    const observer = new MutationObserver(apply);
    observer.observe(streamRoot, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [streamRoot, cursor]);
};
